import { Value } from 'app/types-shared';
import { codeItemToStoreValue, storeValueToCodeItem } from 'app/infrastructure/data/codes';
import { parseAsDateUTC, toISODatewithoutTimezone } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { FavouriteItem } from '../types-store';
import { FavouriteItemWire } from '../types';

export const mapFavouriteFromWire = (wire: Partial<FavouriteItemWire>): FavouriteItem => ({
  id: wire?.code?.v,

  disabled: wire?.disabled
    ? parseAsDateUTC(wire.disabled)
    : undefined,

  parent: codeItemToStoreValue(wire?.parent),
  owners: wire?.owners?.map(codeItemToStoreValue) ?? [],
  scheduleOwner: codeItemToStoreValue(wire?.scheduleOwner),

  createdBy: codeItemToStoreValue(wire?.createdBy),
  created: wire?.created
    ? parseAsDateUTC(wire?.created)
    : undefined,

  modifiedBy: codeItemToStoreValue(wire?.modifiedBy),
  modified: wire?.modified
    ? parseAsDateUTC(wire?.modified)
    : undefined,

  favouriteType: codeItemToStoreValue(wire?.favouriteType),
  reportType: codeItemToStoreValue(wire?.reportType),
  reportName: wire?.reportName,

  ...(wire?.emailSubscribers !== undefined && { emailSubscribers: wire?.emailSubscribers }),
  hasSchedule: wire?.hasSchedule,

  language: (wire?.language?.map(codeItemToStoreValue) ?? [])
    .map(item => ({
      value: item.value,
      description: item.description
    })),
  longDescription: (wire?.language?.map(codeItemToStoreValue) ?? [])
    .map(item => ({
      value: item.value,
      description: item.longDescription
    })),

  dashboards: wire?.dashboards?.map(codeItemToStoreValue) ?? [],
  companyReports: wire?.companyReports?.map(codeItemToStoreValue) ?? [],
  sharedWithUsers: wire?.sharedWithUsers?.map(codeItemToStoreValue) ?? [],
  sharedWithUserGroups: wire?.sharedWithUserGroups?.map(codeItemToStoreValue) ?? [],
  flgEmail: wire?.flgEmail ?? false,
  invalidOwners: wire?.invalidOwners ?? [],
  invalidOwnerInPath: wire?.invalidOwnerInPath ?? false,
  ancestors: wire?.ancestors ?? []
});

export const mapFavouriteToWire = (favourite: FavouriteItem): FavouriteItemWire => ({

  ...(isDefinedIncludingZero(favourite?.id) && {
    code: {
      v: favourite.id
    }
  }),

  disabled: favourite?.disabled
    ? toISODatewithoutTimezone(favourite.disabled)()
    : undefined,

  ...(isDefinedIncludingZero(favourite?.parent?.value) && {
    parent: { v: favourite?.parent?.value, d: favourite?.parent?.description }
  }),

  owners: favourite?.owners?.map(storeValueToCodeItem) ?? [],

  ...(isDefinedIncludingZero(favourite?.scheduleOwner?.value) && {
    scheduleOwner: { v: favourite?.scheduleOwner?.value, d: favourite?.scheduleOwner?.description }
  }),

  createdBy: isDefinedIncludingZero(favourite?.createdBy?.value)
    ? { v: favourite?.createdBy?.value, d: favourite?.createdBy?.description }
    : {} as Value,

  created: favourite?.created
    ? toISODatewithoutTimezone(favourite.created)()
    : undefined,

  modifiedBy: isDefinedIncludingZero(favourite?.modifiedBy?.value)
    ? { v: favourite?.modifiedBy?.value, d: favourite?.modifiedBy?.description }
    : {} as Value,

  modified: favourite?.modified
    ? toISODatewithoutTimezone(favourite.modified)()
    : undefined,

  favouriteType: isDefinedIncludingZero(favourite?.favouriteType?.value)
    ? { v: favourite?.favouriteType.value, d: favourite?.favouriteType?.description }
    : {} as Value,

  reportType: isDefinedIncludingZero(favourite?.reportType?.value)
    ? { v: favourite?.reportType?.value }
    : {} as Value,

  reportName: favourite?.reportName,

  emailSubscribers: favourite?.emailSubscribers,
  hasSchedule: favourite?.hasSchedule,

  dashboards: favourite?.dashboards?.map(storeValueToCodeItem),
  companyReports: favourite?.companyReports?.map(storeValueToCodeItem),
  sharedWithUsers: favourite?.sharedWithUsers?.map(storeValueToCodeItem),
  sharedWithUserGroups: favourite?.sharedWithUserGroups?.map(storeValueToCodeItem),
  flgEmail: favourite?.flgEmail ?? false,
  invalidOwners: favourite?.invalidOwners ?? [],
  invalidOwnerInPath: favourite?.invalidOwnerInPath ?? false,
  ancestors: favourite?.ancestors ?? [],
  language: favourite?.language?.map(languageItem => {
    const longDescription = favourite?.longDescription?.find(longDescriptionItem =>
      longDescriptionItem.value === languageItem.value)?.description?.trim();

    return {
      v: languageItem.value,
      d: languageItem.description?.trim(),
      ...(!!longDescription && { ld: longDescription })
    };
  }) ?? []
});
